import { getAuthDenialCode, handleAuthDenial } from '@Core/Auth/authDenial'
import { ID_TOKEN_EVENT } from '@Core/Hooks/useSession'
import { CookiesStorage } from '@Core/Communication/GlobalStore/CookiesStorage/CookiesStorage'
import axios, { Axios } from 'axios'
import LogRocket from 'logrocket'
import { BaseApi } from 'src/Data/DataSource/API/Entity/BaseAPIEntity'
const API_URL =
    import.meta.env.MODE === 'test'
        ? import.meta.env.VITE_BASE_MOCK_API
        : import.meta.env.VITE_BASE_URL_API || 'https://api-ofc.buzato.dev.br'

const storage = new CookiesStorage()

/** The sign-in itself calls this; waiting on it would wait on the login. */
const SIGN_IN_PROBE = '/user/me/permissions'

/** Resolves with the next ID token, which the session screen produces after a re-login. */
const nextIdToken = () =>
    new Promise<void>((resolve) => {
        window.addEventListener(ID_TOKEN_EVENT, () => resolve(), { once: true })
    })
export class Api implements BaseApi {
    private axios: Axios
    public baseURL: string = ''

    constructor(
        baseURL: string = API_URL as string,
        private readonly tokenKey: string = '@ofc:accessToken'
    ) {
        this.axios = axios.create({
            baseURL,
        })
        this.getInterceptors()
        this.baseURL = baseURL
    }

    /** Only the ODHUB API speaks the session-denial protocol. */
    private get ownsSession() {
        return this.tokenKey === '@ofc:accessToken'
    }

    getSource() {
        return this.baseURL
    }

    getInstance() {
        return this.axios
    }

    getInterceptors() {
        const tokenKey = this.tokenKey
        const ownsSession = this.ownsSession
        const instance = this.axios
        this.axios.interceptors.request.use(
            function (config: any) {
                config.headers.Authorization = `Bearer ${storage.getItem(
                    tokenKey
                )}`
                // Correlaciona a requisição com o backend (Sentry/Discord).
                // Só na API própria: cabeçalhos extras quebram o CORS do Graph.
                if (ownsSession) {
                    config.headers['X-Request-Id'] = crypto.randomUUID()
                    if (LogRocket.sessionURL) {
                        config.headers['X-LogRocket-URL'] = LogRocket.sessionURL
                    }
                }
                // Do something before request is sent
                return config
            },
            function (error: any) {
                // Do something with request error
                return Promise.reject(error)
            }
        )

        this.axios.interceptors.response.use(
            function (response: any) {
                return response
            },
            function (error: any) {
                const status = error.response?.status
                if (import.meta.env.VITE_MOCK_AUTH || !ownsSession) {
                    return Promise.reject(error)
                }
                const denial = getAuthDenialCode(error)
                if (denial) {
                    handleAuthDenial(denial)
                } else if (status === 401) {
                    // 403 without a denial code is a missing permission
                    // (PermissionGuard): the session itself is still valid.
                    storage.removeItem('@ofc:accessToken')
                    // The session-expired screen is up: hold the request and
                    // replay it with the new token, so the user's action is
                    // not lost.
                    const config = error.config
                    if (config && !config.__replayed && config.url !== SIGN_IN_PROBE) {
                        config.__replayed = true
                        return nextIdToken().then(() => instance.request(config))
                    }
                }
                return Promise.reject(error)
            }
        )
    }

    get(url: string, config?: any) {
        return this.axios.get(url, config)
    }

    post(url: string, data?: any, config?: any) {
        return this.axios.post(url, data, config)
    }

    put(url: string, data?: any, config?: any) {
        return this.axios.put(url, data, config)
    }

    delete(url: string, config?: any) {
        return this.axios.delete(url, config)
    }

    patch(url: string, data?: any, config?: any) {
        return this.axios.patch(url, data, config)
    }
}

export const API = new Api()
export type ApiType = typeof API
